import React, { useEffect, useState, useRef } from 'react'
import Modal from '../Modal'
import Button from '../Button'
import Typography from '../Typography'
import { showNotification } from '../Notification'
import PTRestAPI from '../../utils/pt_rest_api'
import DataTable from '../DataTable'
import { PLATFORM_API_URL, PLATFORM_API_VERSION } from '../../config'

const convertToKebabCase = (str) => {
  return str
    .trim() // Remove leading and trailing spaces
    .toLowerCase() // Convert to lowercase
    .replace(/\s+/g, '-') // Replace multiple spaces with a single hyphen
    .replace(/^-|-$/g, '') // Remove any leading or trailing hyphens
}

export default ({ controller }) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [thumbnail, setThumbnail] = useState(null)
  const [errors, setErrors] = useState({})
  const [categoryID, setCategoryID] = useState('')
  const [objectID, setObjectID] = useState('')
  const [tableRefresh, setTableRefresh] = useState(false)
  const fileInputRef = useRef(null)

  const categoryRender = [
    {
      id: 'thumbnail',
      title: 'Thumbnail',
      headerClassName: 'w-[100px]',
      render: (value) => {
        value = `${PLATFORM_API_URL}/${PLATFORM_API_VERSION}/blog-resource?file=${value}&cache=${Date.now()}`
        return (
          <img src={value} alt="Thumbnail" className="w-16 h-16 object-cover" />
        )
      },
    },
    {
      id: 'title',
      title: 'Title',
      headerClassName: 'w-[500px]',
      render: (value) => <Typography className="text-text">{value}</Typography>,
    },
    {
      id: 'load',
      title: '',
      headerClassName: 'w-[100px]',
      render: (value, item) => {
        return (
          <Button
            variant="action"
            onClick={() => loadCategory(item.categoryID)}
          >
            Load
          </Button>
        )
      },
    },
  ]

  const loadCategory = async (categoryID) => {
    if (!categoryID) return

    if (categoryID !== '~new') {
      try {
        const categoryPayload = await controller.loadCategory(categoryID)

        if (categoryPayload.error) {
          showNotification({
            message: 'Error loading category data',
            className: 'bg-red-500 text-text_inv',
          })
          return
        }

        setTitle(categoryPayload.title)
        setDescription(categoryPayload.description)
        setCategoryID(categoryPayload.categoryID)
        setObjectID(categoryPayload._id)
      } catch (error) {
        console.log(error)
        showNotification({
          message: 'Error loading category data',
          className: 'bg-red-500 text-text_inv',
        })
      }
    } else {
      setTitle('')
      setDescription('')
      setThumbnail(null)
      setObjectID('')
      fileInputRef.current.value = ''
      setCategoryID('~new')
    }
  }

  //refresh on open
  useEffect(() => {
    if (controller.categoryOpen) {
      setTableRefresh(!tableRefresh)
      loadCategory('~new')
    }
  }, [controller.categoryOpen])

  useEffect(() => {
    loadCategory(controller.categoryID)
  }, [controller.categoryID, controller.refreshKey])

  const handleSubmit = async () => {
    setErrors({})

    let hasError = false
    const newErrors = {}

    if (categoryID === '' || categoryID === '~new') {
      newErrors.categoryID = 'Category ID is required'
      hasError = true
    }

    if (title === '') {
      newErrors.title = 'Title is required'
      hasError = true
    }

    if (description === '') {
      newErrors.description = 'Description is required'
      hasError = true
    }

    if (hasError) {
      setErrors(newErrors)
      showNotification({
        message: 'Please correct the highlighted fields above.',
        className: 'bg-red-500 text-text_inv',
      })
      return
    }

    const updatedPayload = {
      categoryID: convertToKebabCase(categoryID),
      title,
      description,
      thumbnail,
      objectID,
    }

    Object.keys(updatedPayload).forEach((key) => {
      if (!updatedPayload[key]) {
        delete updatedPayload[key]
      }
    })

    try {
      const result = await controller.updateCategory(updatedPayload)

      if (result.error) {
        showNotification({
          message: 'Error saving category',
          className: 'bg-red-500 text-text_inv',
        })
        return
      }
      setTableRefresh(!tableRefresh)
      showNotification({
        message: 'Category updated successfully',
        className: 'bg-primary text-text_inv',
      })
    } catch (error) {
      showNotification({
        message: 'Error saving category',
        className: 'bg-red-500 text-text_inv',
      })
    }
  }

  return (
    <Modal
      isOpen={controller.categoryOpen}
      onCloseRequested={controller.setCategoryOpen}
      closeOnEscape={true}
      className="w-full sm:w-[500px] md:absolute md:top-[65px] h-full md:h-auto sm:max-w-[960px] bg-paper text-sm sm:text-base z-200"
    >
      <div>
        <Typography variant="h3" className="flex-1 text-text mb-4">
          Categories
        </Typography>

        <div className="flex flex-row gap-5">
          <div className="flex flex-1 flex-col gap-2">
            <Button variant={'action'} onClick={controller.newCategory}>
              New Category
            </Button>
            {/* Update form */}
            <form>
              <div className="mb-4">
                <label
                  className={`block font-bold mb-2 ${
                    errors.categoryID ? 'text-error' : 'text-text_secondary'
                  }`}
                >
                  Category ID
                </label>
                <input
                  type="text"
                  value={categoryID === '~new' ? '' : categoryID}
                  onChange={(e) => setCategoryID(e.target.value)}
                  className={`outline-none text-text bg-neutral_2 border ${
                    errors.categoryID ? 'border-error' : 'border-bordered'
                  } rounded p-2 w-full`}
                />
              </div>
              <div className="mb-4">
                <label
                  className={`block font-bold mb-2 ${
                    errors.title ? 'text-error' : 'text-text_secondary'
                  }`}
                >
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={`outline-none text-text bg-neutral_2 border ${
                    errors.title ? 'border-error' : 'border-bordered'
                  } rounded p-2 w-full`}
                />
              </div>

              <div className="mb-4">
                <label
                  className={`block font-bold mb-2 ${
                    errors.description ? 'text-error' : 'text-text_secondary'
                  }`}
                >
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={`outline-none text-text bg-neutral_2 border ${
                    errors.description ? 'border-error' : 'border-bordered'
                  } rounded p-2 w-full`}
                  rows="4"
                ></textarea>
              </div>

              <div className="mb-4">
                <label
                  className={`block font-bold mb-2 ${
                    errors.thumbnail ? 'text-error' : 'text-text_secondary'
                  }`}
                >
                  Thumbnail
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setThumbnail(e.target.files[0])}
                  className={`outline-none text-text bg-neutral_2 border ${
                    errors.thumbnail ? 'border-error' : 'border-bordered'
                  } rounded p-2 w-full`}
                />
              </div>

              <div className="mt-5">
                <div className="flex flex-row gap-5">
                  <Button
                    variant="action"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSubmit()
                    }}
                    className="w-full"
                  >
                    {objectID ? 'Update Category' : 'Create Category'}
                  </Button>
                </div>
                {Object.keys(errors).length > 0 && (
                  <p className="text-error mb-4 text-center">
                    Please correct the highlighted fields above.
                  </p>
                )}
              </div>
            </form>
          </div>
          {/* List categories */}

          <div className="flex-1 min-h-[100px]">
            <DataTable
              title={'Available'}
              refreshKey={tableRefresh}
              preferredHeight="400px"
              itemHeight="h-[20px]"
              apiMethod={PTRestAPI.ListCategories}
              apiDataField={'categories'}
              searchField={'title'}
              renders={categoryRender}
              noData={
                <Typography variant="h5" className="text-text">
                  {`No categories found`}
                </Typography>
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
